import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Row, Col, Alert, Card, CardBody, Badge } from 'reactstrap';
import { H6 } from '../../../../AbstractElements';

const EditQuizModal = ({ isOpen, toggle, editingQuiz, formData, setFormData, onSubmit }) => {
  const [previewUrl, setPreviewUrl] = useState(editingQuiz?.thumbnailUrl || formData.thumbnailUrl || null);
  const [forceRender, setForceRender] = useState(0);
  
  const [questions, setQuestions] = useState(() => {
    // Transform API data structure to form structure
    if (formData.questions && formData.questions.length > 0) {
      return formData.questions.map((q, index) => {
        return {
          id: q.id, // GIỮ LẠI ID để backend biết cập nhật question nào
          content: typeof q.content === 'string' ? q.content : q.content?.text || '',
          type: q.type,
          timeLimit: q.timeLimit ?? undefined, // Không set mặc định 30 khi load
          mediaFile: null,
          mediaPreview: q.mediaUrl || null,
          answers: q.answers?.map((a, aIndex) => {
            return {
              text: a.content || a.text || '',
              isCorrect: a.isCorrect
            };
          }) || []
        };
      });
    }
    return [];
  });
  
  const [questionMediaFiles, setQuestionMediaFiles] = useState([]);
  const [timeLimitErrors, setTimeLimitErrors] = useState({});

  // Update previewUrl when formData changes
  useEffect(() => {
    if (formData.thumbnailUrl) {
      setPreviewUrl(formData.thumbnailUrl);
    }
  }, [formData.thumbnailUrl]);

  // Force re-render when questions change
  useEffect(() => {
    setForceRender(prev => prev + 1);
  }, [questions]);

  // Update questions when formData changes
  useEffect(() => {
    if (formData.questions && formData.questions.length > 0) {
      const transformedQuestions = formData.questions.map((q, index) => {
        return {
          id: q.id, 
          content: typeof q.content === 'string' ? q.content : q.content?.text || '',
          type: q.type,
          timeLimit: q.timeLimit ?? undefined, // Không set mặc định 30 khi transform
          mediaFile: null,
          mediaPreview: q.mediaUrl || null,
          answers: q.answers?.map(a => {
            return {
              text: a.content || a.text || '',
              isCorrect: a.isCorrect
            };
          }) || []
        };
      });
      setQuestions(transformedQuestions);
    }
  }, [formData.questions, editingQuiz]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Vui lòng chọn file hình ảnh');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size không được vượt quá 5MB');
        return;
      }

      setFormData({ ...formData, thumbnail: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleQuestionMediaChange = (qIndex, file) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Vui lòng chọn file hình ảnh');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size không được vượt quá 5MB');
        return;
      }

      const newQuestions = [...questions];
      newQuestions[qIndex].mediaFile = file;
      
      const reader = new FileReader();
      reader.onloadend = () => {
        newQuestions[qIndex].mediaPreview = reader.result;
        setQuestions([...newQuestions]);
      };
      reader.readAsDataURL(file);
      
      const newMediaFiles = [...questionMediaFiles];
      newMediaFiles[qIndex] = file;
      setQuestionMediaFiles(newMediaFiles);
      setFormData({ ...formData, questionMedias: newMediaFiles });
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      content: '',
      type: 'single',
      timeLimit: 30,
      mediaFile: null,
      mediaPreview: null,
      answers: [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false }
      ]
    };
    const newQuestions = [...questions, newQuestion];
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveQuestion = (qIndex) => {
    const newQuestions = questions.filter((_, idx) => idx !== qIndex);
    const newMediaFiles = questionMediaFiles.filter((_, idx) => idx !== qIndex);
    setQuestions(newQuestions);
    setQuestionMediaFiles(newMediaFiles);
    setFormData({ ...formData, questions: newQuestions, questionMedias: newMediaFiles });
  };

  const handleQuestionChange = (qIndex, field, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex][field] = value;
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleAddAnswer = (qIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers.push({ text: '', isCorrect: false });
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveAnswer = (qIndex, aIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers = newQuestions[qIndex].answers.filter((_, idx) => idx !== aIndex);
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleAnswerChange = (qIndex, aIndex, field, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers[aIndex][field] = value;
    
    if (field === 'isCorrect' && value && newQuestions[qIndex].type === 'single') {
      newQuestions[qIndex].answers.forEach((answer, idx) => {
        if (idx !== aIndex) answer.isCorrect = false;
      });
    }
    
    setQuestions(newQuestions);
    setFormData({ ...formData, questions: newQuestions });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      style={{ maxWidth: '90%' }}
      centered
      modalTransition={{ timeout: 200 }}
      backdropTransition={{ timeout: 150 }}
    >
      <ModalHeader toggle={toggle}>
        Chỉnh sửa Quiz Template
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody style={{maxHeight: '70vh', overflowY: 'auto'}}>
          <Row>
            {/* Basic Info */}
            <Col md="12">
              <Card className="mb-3">
                <CardBody>
                  <H6 className="mb-3">Thông tin cơ bản</H6>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Tên Quiz Template *</Label>
                        <Input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Quiz Kiến Thức Y Dược"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Thumbnail (Optional)</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        <small className="text-muted">Để trống nếu không đổi. Max 5MB</small>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Số câu hỏi hiển thị *</Label>
                        <Input
                          type="number"
                          value={formData.displayQuestions}
                          onChange={(e) => setFormData({...formData, displayQuestions: parseInt(e.target.value)})}
                          min="1"
                          required
                        />
                        <small className="text-muted">Random từ tổng số câu hỏi</small>
                      </FormGroup>
                    </Col>
                    {previewUrl && (
                      <Col md="12">
                        <div className="border rounded p-2">
                          <img 
                            src={previewUrl} 
                            alt="Thumbnail" 
                            className="img-fluid rounded"
                            style={{maxHeight: '200px', width: '100%', objectFit: 'contain'}}
                          />
                        </div>
                      </Col>
                    )}
                    <Col md="12">
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        />
                        <Label className="form-check-label">
                          {formData.isActive ? 'Active' : 'Inactive'}
                        </Label>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Questions */}
            <Col md="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <H6 className="mb-0">Câu hỏi ({questions.length})</H6>
                    <Button color="success" size="sm" onClick={handleAddQuestion}>
                      <i className="fa fa-plus me-1"></i>
                      Thêm câu hỏi
                    </Button>
                  </div>

                  {questions.map((question, qIndex) => (
                    <Card key={qIndex} className="mb-3 border">
                      <CardBody className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <Badge color="primary">Câu {qIndex + 1}</Badge>
                          <Button 
                            color="danger" 
                            size="sm"
                            onClick={() => handleRemoveQuestion(qIndex)}
                          >
                            <i className="fa fa-trash"></i>
                          </Button>
                        </div>

                        <Row>
                          <Col md="12">
                            <FormGroup>
                              <Label>Nội dung câu hỏi *</Label>
                              <Input
                                type="textarea"
                                rows="2"
                                value={question.content}
                                onChange={(e) => handleQuestionChange(qIndex, 'content', e.target.value)}
                                placeholder="Nhập nội dung câu hỏi..."
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>Loại câu hỏi</Label>
                              <Input
                                type="select"
                                value={question.type}
                                onChange={(e) => handleQuestionChange(qIndex, 'type', e.target.value)}
                              >
                                <option value="single">Single Choice (1 đáp án đúng)</option>
                                <option value="multi">Multiple Choice (nhiều đáp án đúng)</option>
                              </Input>
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>Thời gian giới hạn (giây) *</Label>
                              <Input
                                type="number"
                                value={question.timeLimit ?? ''}
                                invalid={!!timeLimitErrors[qIndex]}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  // Xóa lỗi khi bắt đầu nhập
                                  if (timeLimitErrors[qIndex]) {
                                    setTimeLimitErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[qIndex];
                                      return newErrors;
                                    });
                                  }
                                  
                                  if (val === '') {
                                    handleQuestionChange(qIndex, 'timeLimit', undefined);
                                  } else {
                                    const num = parseInt(val);
                                    if (!isNaN(num) && num > 0) {
                                      handleQuestionChange(qIndex, 'timeLimit', num);
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  if (!val || val === '' || !question.timeLimit || question.timeLimit < 1 || question.timeLimit > 300) {
                                    setTimeLimitErrors(prev => ({
                                      ...prev,
                                      [qIndex]: 'Vui lòng nhập thời gian giới hạn từ 1 đến 300 giây'
                                    }));
                                    // BỎ phần tự động set về 30 - để người dùng tự nhập
                                  } else {
                                    // Xóa lỗi nếu hợp lệ
                                    setTimeLimitErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[qIndex];
                                      return newErrors;
                                    });
                                  }
                                }}
                                min="1"
                                max="300"
                                required
                              />
                              {timeLimitErrors[qIndex] && (
                                <small className="text-danger d-block mt-1">
                                  <i className="fa fa-exclamation-circle me-1"></i>
                                  {timeLimitErrors[qIndex]}
                                </small>
                              )}
                              {!timeLimitErrors[qIndex] && (
                                <small className="text-muted">Thời gian tối đa để trả lời câu hỏi này (giây)</small>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label>Hình ảnh câu hỏi (tùy chọn)</Label>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleQuestionMediaChange(qIndex, e.target.files[0])}
                              />
                              <small className="text-muted">Max 5MB. JPG, PNG, GIF, WebP</small>
                            </FormGroup>
                          </Col>
                          {question.mediaPreview && (
                            <Col md="12">
                              <div className="border rounded p-2 mb-2">
                                <img 
                                  src={question.mediaPreview} 
                                  alt={`Question ${qIndex + 1} media`}
                                  className="img-fluid rounded"
                                  style={{maxHeight: '200px', width: '100%', objectFit: 'contain'}}
                                />
                              </div>
                            </Col>
                          )}
                          <Col md="12">
                            <Label className="d-flex justify-content-between align-items-center">
                              <span>Đáp án</span>
                              <Button 
                                color="success" 
                                size="sm"
                                onClick={() => handleAddAnswer(qIndex)}
                              >
                                <i className="fa fa-plus me-1"></i>
                                Thêm đáp án
                              </Button>
                            </Label>
                            {question.answers.map((answer, aIndex) => {
                              return (
                                <div key={`${qIndex}-${aIndex}`} className="d-flex gap-2 mb-2">
                                  <div className="form-check d-flex align-items-center">
                                    {question.type === 'single' ? (
                                      <Input
                                        key={`${qIndex}-${aIndex}-${forceRender}`}
                                        type="radio"
                                        className="form-check-input"
                                        checked={Boolean(answer.isCorrect)}
                                        onChange={(e) => {
                                          handleAnswerChange(qIndex, aIndex, 'isCorrect', e.target.checked);
                                        }}
                                        name={`question-${qIndex}-correct`}
                                        id={`question-${qIndex}-answer-${aIndex}`}
                                        style={{transform: 'scale(1.2)'}}
                                      />
                                    ) : (
                                      <div 
                                        className="form-check-input"
                                        style={{
                                          width: '20px',
                                          height: '20px',
                                          border: '2px solid #007bff',
                                          borderRadius: '3px',
                                          backgroundColor: answer.isCorrect ? '#007bff' : 'white',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transform: 'scale(1.2)'
                                        }}
                                        onClick={() => {
                                          handleAnswerChange(qIndex, aIndex, 'isCorrect', !answer.isCorrect);
                                        }}
                                      >
                                        {answer.isCorrect && (
                                          <i className="fa fa-check text-white" style={{fontSize: '12px'}}></i>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                <Input
                                  type="text"
                                  value={answer.text}
                                  onChange={(e) => handleAnswerChange(qIndex, aIndex, 'text', e.target.value)}
                                  placeholder={`Đáp án ${String.fromCharCode(65 + aIndex)}`}
                                  required
                                />
                                <Button 
                                  color="danger" 
                                  size="sm"
                                  onClick={() => handleRemoveAnswer(qIndex, aIndex)}
                                  disabled={question.answers.length <= 2}
                                >
                                  <i className="fa fa-trash"></i>
                                </Button>
                                </div>
                              );
                            })}
                            <small className="text-muted">
                              {question.type === 'single' 
                                ? 'Chọn 1 đáp án đúng' 
                                : 'Có thể chọn nhiều đáp án đúng'}
                            </small>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  ))}

                  {questions.length === 0 && (
                    <Alert color="info">
                      <i className="fa fa-info-circle me-2"></i>
                      Chưa có câu hỏi nào. Nhấn "Thêm câu hỏi" để bắt đầu.
                    </Alert>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Info: Rewards management */}
            <Col md="12">
              <Alert color="info">
                <i className="fa fa-gift me-2"></i>
                <strong>Quản lý Rewards:</strong> Để thêm/sửa/xóa rewards cho quiz này, vui lòng vào trang <strong>Rewards Management</strong>.
              </Alert>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Hủy
          </Button>
          <Button color="primary" type="submit" disabled={questions.length === 0}>
            <i className="fa fa-save me-2"></i>
            Cập nhật Quiz
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditQuizModal;

